import { Injectable, signal } from '@angular/core';
import { msalConfig } from '../auth-config';
import { PublicClientApplication, AuthenticationResult, AccountInfo, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private msalInstance: PublicClientApplication;

  readonly username = signal<string>('');

  private isRedirectInProgress = false;

  private tokenRenewalHandle: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight: Promise<void> | null = null;

  constructor() {
    // Inicializar MSAL instancia
    this.msalInstance = new PublicClientApplication(msalConfig);

    this.msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS
      ) {
        const result = event.payload as AuthenticationResult;
        const account = result?.account;
        if (account) {
          this.msalInstance.setActiveAccount(account);
          this.username.set(account.name ?? '');
        }
      }

      if (event.eventType === EventType.HANDLE_REDIRECT_END) {
        this.updateUserState();
        this.isRedirectInProgress = false;
      }
    });
  }

  async initializeMsal(): Promise<void> {
    await this.msalInstance.initialize();

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalInstance.setActiveAccount(accounts[0]);
    }

    this.updateUserState();
  }

  isInitialized(): boolean {
    console.log('msalInstanceInitialized', !!this.msalInstance)
    return !!this.msalInstance;
  }

  async handleRedirectResult(): Promise<void> {
    await this.initializeMsal();

    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response && response.account) {
        this.msalInstance.setActiveAccount(response.account);
        localStorage.setItem('idToken', response.idToken);
      } else {
        const activeAccount = this.msalInstance.getActiveAccount();
        if (activeAccount) {
          this.msalInstance.setActiveAccount(activeAccount);
        }
      }
    } catch (error) {
      console.error('Error durante el manejo del redireccionamiento:', error);
    }
  }

  async login(): Promise<void> {
    await this.initializeMsal();
    this.msalInstance.loginRedirect();
  }

  async logout(): Promise<void> {
    await this.initializeMsal();
    this.msalInstance.logoutRedirect();
  }

  isAuthenticated(): boolean {
    const activeAccount = this.msalInstance.getActiveAccount();
    return !!activeAccount;
  }

  getActiveAccount(): AccountInfo | null {
    try {
      const accounts = this.msalInstance.getAllAccounts(); // Obtener todas las cuentas desde el cache

      if (accounts.length === 0) {
        return null;
      }

      const activeAccount = this.msalInstance.getActiveAccount();
      if (!activeAccount) {
        this.msalInstance.setActiveAccount(accounts[0]); // Configurar manualmente la primera cuenta activa
        return accounts[0]; // Retorna la cuenta configurada
      }

      return activeAccount; // Retorna la cuenta activa configurada
    } catch (error) {
      console.error('Error mientras se intentaba obtener la cuenta activa:', error);
      return null; // En caso de error, retorna null
    }
  }

  getIdToken(): string | null {
    const activeAccount = this.getActiveAccount();
    return activeAccount ? localStorage.getItem('idToken') : null;
  }

  async acquireNewToken(): Promise<void> {
    try {
      const account = this.msalInstance.getActiveAccount();
      if (!account) return;

      const result = await this.msalInstance.acquireTokenSilent({
        account,
        scopes: ['openid', 'profile'],
      });

      localStorage.setItem('idToken', result.idToken);
      this.scheduleTokenRenewal(result.idToken);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError && !this.isRedirectInProgress) {
        this.isRedirectInProgress = true;
        await this.msalInstance.acquireTokenRedirect({
          scopes: ['openid', 'profile'],
          loginHint: this.msalInstance.getActiveAccount()?.username,
        });
      } else {
        console.error('Error al renovar el token:', error);
      }
    }
  }

  private updateUserState(): void {
    const activeAccount = this.msalInstance.getActiveAccount();
    this.username.set(activeAccount?.name ?? '');
  }

  private decodeToken(token: string): { [key: string]: any } {
  try {
    const base64Url = token.split('.')[1]; // Obtener la parte payload del token JWT
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); // Decodificar base64
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload); // Retorna el payload como un objeto
  } catch (error) {
    console.error('Error al decodificar el token:', error);
    return {}; // Retornar un objeto vacío en caso de error
  }
  }

  public isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);

    if (!payload['exp']) {
      console.warn('El token no tiene campo de expiración (exp). Asumiendo que está expirado.');
      return true; // Si no hay campo `exp`, asumimos que está expirado
    }

    const currentTime = Math.floor(Date.now() / 1000); // Tiempo actual en segundos
    return payload['exp'] < currentTime; // true si ha expirado
  }

  private getTokenExpiration(token: string): number {
    const payload = this.decodeToken(token);

    if (!payload['exp']) {
      console.warn('El token no tiene campo de expiración (exp).');
      return 0; // Retorna 0 si no hay campo `exp`
    }

    return payload['exp']; // Retorna el Unix timestamp de expiración
  }

  public scheduleTokenRenewal(token: string): void {
    const expirationTime = this.getTokenExpiration(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const timeLeft = expirationTime - currentTime;
    const renewalMargin = 300;
    const renewalTime = (timeLeft - renewalMargin) * 1000;

    if (this.tokenRenewalHandle) {
      clearTimeout(this.tokenRenewalHandle);
      this.tokenRenewalHandle = null;
    }

    if (renewalTime <= 0) {
      this.acquireNewTokenSafely();
    } else {
      this.tokenRenewalHandle = setTimeout(() => {
        this.tokenRenewalHandle = null;
        this.acquireNewTokenSafely();
      }, renewalTime);
    }
  }

  private acquireNewTokenSafely(): void {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.acquireNewToken()
        .catch((error) => console.error('Error al renovar el token:', error))
        .finally(() => (this.refreshInFlight = null));
    }
  }
}
